import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError, api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Toast, ToastDescription, ToastTitle } from '@/components/ui/toast'
import {
  PROMPT_BANNED_PATTERNS_DEFAULT,
  findBannedPattern,
  type Campaign,
  type CampaignListResponse,
  type PromptResponse,
} from '@sdr-agend/admin-schemas'
import { usePromptConfig } from '@/hooks/usePromptConfig'

interface ToastState {
  open: boolean
  variant: 'default' | 'destructive'
  title: string
  description?: string
}

interface NewPromptDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * Optional initial value for the system prompt textarea. Story 3.18 uses this
   * so the PromptAssistant wizard can hand off its generated draft to this
   * dialog for manual editing before submission.
   */
  defaultSystemPrompt?: string
  /**
   * Optional initial campaign id. When set, the dropdown is pre-selected.
   * Useful when this dialog is launched from a campaign-scoped UI.
   */
  defaultCampaignId?: string
}

/**
 * Story 3.15 — "Novo Prompt" modal.
 *
 * Closes the TODO of Story 3.7 by giving managers/admins a UI path to bind the
 * first prompt of a campaign without hitting the API by curl. Reuses the
 * validation primitives from `PromptDetail.tsx` (banned pattern + char range)
 * and the existing `POST /admin/prompts` endpoint — zero backend change.
 */
export function NewPromptDialog({
  open,
  onOpenChange,
  defaultSystemPrompt = '',
  defaultCampaignId = '',
}: NewPromptDialogProps): React.ReactElement {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const titleId = useId()
  const descriptionId = useId()
  const initialInputRef = useRef<HTMLSelectElement | null>(null)

  const [campaignId, setCampaignId] = useState<string>(defaultCampaignId)
  const [systemPrompt, setSystemPrompt] = useState<string>(defaultSystemPrompt)
  const [toast, setToast] = useState<ToastState>({
    open: false,
    variant: 'default',
    title: '',
  })

  // Reset state whenever the dialog opens — protects the form from stale
  // values when the parent re-opens after a previous create attempt.
  useEffect(() => {
    if (open) {
      setCampaignId(defaultCampaignId)
      setSystemPrompt(defaultSystemPrompt)
    }
  }, [open, defaultCampaignId, defaultSystemPrompt])

  // Fetch all campaigns — list is small (<50 in production per Dev Notes #2),
  // so we filter client-side rather than adding a `without_prompt=true` query
  // param to the backend (decision Dev Notes #2).
  const { data: campaignsData, isLoading: campaignsLoading } = useQuery<CampaignListResponse>({
    queryKey: ['admin-campaigns', { withoutPrompt: true }],
    queryFn: async () => api<CampaignListResponse>('/admin/campaigns'),
    enabled: open,
  })

  const availableCampaigns: Campaign[] = useMemo(() => {
    return (campaignsData?.campaigns ?? []).filter((c) => c.config.prompt_id === '')
  }, [campaignsData])

  const blockedCampaigns: Campaign[] = useMemo(() => {
    return (campaignsData?.campaigns ?? []).filter((c) => c.config.prompt_id !== '')
  }, [campaignsData])

  function notify(state: Omit<ToastState, 'open'>): void {
    setToast({ open: true, ...state })
  }

  const createMutation = useMutation({
    mutationFn: async () =>
      api<PromptResponse>('/admin/prompts', {
        method: 'POST',
        body: JSON.stringify({ campaignId, systemPrompt }),
      }),
    onSuccess: (res) => {
      notify({
        variant: 'default',
        title: 'Prompt criado',
        description: 'A nova versão já está ativa para a campanha.',
      })
      void queryClient.invalidateQueries({ queryKey: ['admin-prompts'] })
      void queryClient.invalidateQueries({ queryKey: ['admin-campaigns'] })
      onOpenChange(false)
      navigate(`/admin/prompts/${res.prompt.id}`)
    },
    onError: (err: Error) => {
      const status = err instanceof ApiError ? err.status : 0
      if (status === 409) {
        notify({
          variant: 'destructive',
          title: 'Campanha já possui prompt ativo',
          description: 'Use a edição na tela de detalhe para criar uma nova versão.',
        })
        return
      }
      const msg = err instanceof ApiError ? formatApiError(err) : err.message
      notify({ variant: 'destructive', title: 'Falha ao criar prompt', description: msg })
    },
  })

  const { maxChars, minChars } = usePromptConfig()
  const bannedHit = findBannedPattern(systemPrompt, PROMPT_BANNED_PATTERNS_DEFAULT)
  const tooShort = systemPrompt.length < minChars
  const tooLong = systemPrompt.length > maxChars
  const canSubmit =
    campaignId !== '' && !tooShort && !tooLong && bannedHit === null && !createMutation.isPending

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          aria-labelledby={titleId}
          aria-describedby={descriptionId}
          onOpenAutoFocus={(event) => {
            event.preventDefault()
            initialInputRef.current?.focus()
          }}
        >
          <DialogHeader>
            <DialogTitle id={titleId}>Novo prompt</DialogTitle>
            <DialogDescription id={descriptionId}>
              Selecione a campanha e escreva o system prompt inicial. Versões posteriores
              podem ser criadas a partir da tela de detalhe.
            </DialogDescription>
          </DialogHeader>

          <form
            className="grid gap-4"
            onSubmit={(e) => {
              e.preventDefault()
              if (canSubmit) createMutation.mutate()
            }}
          >
            <div className="grid gap-2">
              <Label htmlFor="np-campaign">Campanha</Label>
              <Select
                id="np-campaign"
                ref={initialInputRef}
                value={campaignId}
                onChange={(e) => setCampaignId(e.target.value)}
                disabled={campaignsLoading || createMutation.isPending}
                required
                aria-describedby="np-campaign-hint"
              >
                <option value="">
                  {campaignsLoading ? 'Carregando…' : 'Selecione uma campanha'}
                </option>
                {availableCampaigns.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
                {blockedCampaigns.map((c) => (
                  <option key={c.id} value={c.id} disabled>
                    {c.name} (já tem prompt)
                  </option>
                ))}
              </Select>
              <p id="np-campaign-hint" className="text-xs text-muted-foreground">
                Apenas campanhas sem prompt ativo podem receber a primeira versão.
              </p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="np-system-prompt">System Prompt</Label>
              <Textarea
                id="np-system-prompt"
                value={systemPrompt}
                onChange={(e) => setSystemPrompt(e.target.value)}
                rows={10}
                disabled={createMutation.isPending}
                placeholder="Você é um SDR experiente. Use {{customer_name}} para personalizar…"
                aria-describedby="np-prompt-meta np-prompt-warning"
                required
              />
              <p id="np-prompt-meta" className="text-xs text-muted-foreground">
                {systemPrompt.length} / {maxChars} caracteres (mínimo {minChars})
              </p>
              {bannedHit !== null && (
                <p
                  id="np-prompt-warning"
                  role="alert"
                  className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive"
                >
                  Padrão proibido detectado: <strong>{bannedHit}</strong>. Remova antes de
                  salvar.
                </p>
              )}
              {tooShort && systemPrompt.length > 0 && (
                <p className="text-xs text-amber-600">
                  Mínimo de {minChars} caracteres ainda não atingido.
                </p>
              )}
              {tooLong && (
                <p className="text-xs text-destructive">
                  Excedeu o tamanho máximo de {maxChars} caracteres.
                </p>
              )}
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={createMutation.isPending}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={!canSubmit}>
                {createMutation.isPending ? 'Criando…' : 'Criar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Toast
        open={toast.open}
        onOpenChange={(o) => setToast((s) => ({ ...s, open: o }))}
        variant={toast.variant}
      >
        <div className="grid gap-1">
          <ToastTitle>{toast.title}</ToastTitle>
          {toast.description !== undefined && (
            <ToastDescription>{toast.description}</ToastDescription>
          )}
        </div>
      </Toast>
    </>
  )
}

function formatApiError(err: ApiError): string {
  if (err.bodyJson !== null && typeof err.bodyJson === 'object' && 'error' in err.bodyJson) {
    const e = (err.bodyJson as { error: { code?: string; message?: string } }).error
    return e.message ?? e.code ?? err.message
  }
  return err.message
}

export default NewPromptDialog
